import React from "react";
import PropTypes from "prop-types";
import injectSheet from "react-jss";

import Main from "../components/Main";
import Article from "../components/Main/Article";
import PageHeader from "../components/Page/PageHeader";
import Content from "../components/Main/Content";
import Footer from "../components/Footer/Footer";

const styles = theme => ({});

const Support = () => {
  return (
    <Main>
      <Article>
        <PageHeader title="Privacy Policy" />
        <Content>
          <hr class="section-divider" />

          <h1>Privacy Policy</h1>
          <p class="meta">
            Effective Date: August 21, 2024 &nbsp;·&nbsp; Last Updated: June 5, 2026 &nbsp;·&nbsp;
            Site: <a href="https://www.projectsurfhelmet.com">projectsurfhelmet.com</a>
          </p>

          <div class="highlight-box">
            <strong>The short version:</strong> This is a personal hobby site. I don't collect much.
            I don't sell anything. StatCounter tracks anonymized site traffic. Affiliate links set
            retailer cookies I don't control. Cookie consent is managed through Cookiebot. If you're
            in the EU, California, Canada, or Australia — your rights are covered below. Questions?
            Email me.
          </div>

          <h2>1. Who I Am (Data Controller)</h2>
          <p>
            Project Surf Helmet (projectsurfhelmet.com) is a personal hobby site operated by one
            individual. I am not a company. For the purposes of privacy law, I am the sole data
            controller for any personal information processed through this site.
          </p>
          <div class="contact-block">
            <strong>Cole Merrick</strong> — Personal hobby site operator<br />
            Long Beach, CA, USA<br />
            <a href="mailto:cole@projectsurfhelmet.com">cole@projectsurfhelmet.com</a>
          </div>

          <h2>2. What Data I Collect and Why</h2>

          <h3>Analytics</h3>
          <p>
            This site uses <strong>StatCounter</strong> to understand traffic patterns — which pages
            are popular, roughly where visitors come from, and how the site is used. StatCounter
            collects anonymized data including IP addresses (which StatCounter may use to derive
            approximate location), browser type, and pages visited. I do not use this data to
            identify individual visitors.
          </p>

          <h3>Cookies &amp; Consent Management</h3>
          <p>
            Cookie consent on this site is managed through <strong>Cookiebot</strong>, which gives
            you granular control over which cookies you allow before they are set. The site uses the
            following types of cookies:
          </p>
          <ul>
            <li>
              <strong>Necessary cookies</strong> — Required for the site to function (including
              Cookiebot's own consent cookie, which remembers your preferences).
            </li>
            <li>
              <strong>Statistics cookies</strong> — Set by StatCounter to track anonymized visit
              patterns.
            </li>
            <li>
              <strong>Affiliate cookies</strong> — When you click an affiliate link (e.g., to
              Amazon), the retailer sets a cookie to attribute any resulting purchase so I can
              receive a small commission. I do not set or control these cookies — they are governed
              by the retailer's own privacy policy.
            </li>
          </ul>
          <p>
            You can change or withdraw your cookie consent at any time via the cookie banner on this
            site, or by adjusting your browser settings. Withdrawing consent for statistics cookies
            does not affect your ability to read the site.
          </p>

          <h3>Contacting Me</h3>
          <p>
            The only way to contact me directly is by email:
            <a href="mailto:cole@projectsurfhelmet.com">cole@projectsurfhelmet.com</a>. There is no
            contact form. If you email me, I receive your email address and message. I use this only
            to reply to you and do not share it with anyone.
          </p>

          <h3>Affiliate Links</h3>
          <p>
            Clicking an affiliate link on this site may pass a referral identifier to the linked
            retailer (e.g., Amazon). What that retailer does with that data is governed by their
            privacy policy, not mine.
          </p>

          <h2>3. What I Do NOT Do</h2>
          <ul>
            <li>I do not sell your personal information — ever.</li>
            <li>I do not run advertising networks or serve behaviorally targeted ads.</li>
            <li>I do not build personal profiles of visitors.</li>
            <li>I do not send marketing emails.</li>
            <li>I do not collect payment information.</li>
            <li>I do not have user accounts or registration.</li>
            <li>
              I do not collect sensitive data (no SSNs, financial details, health data, etc.).
            </li>
          </ul>

          <h2>4. Third-Party Services</h2>
          <p>
            The following third-party services may collect data independently per their own
            policies:
          </p>
          <ul>
            <li>
              <strong>StatCounter</strong> — Anonymized analytics.
              <a href="https://statcounter.com/about/legal/" target="_blank" rel="noopener">
                StatCounter Privacy Policy
              </a>. You can opt out at{" "}
              <a
                href="https://statcounter.com/about/set-refusal-cookie/"
                target="_blank"
                rel="noopener"
              >
                statcounter.com/about/set-refusal-cookie
              </a>.
            </li>
            <li>
              <strong>Cookiebot (Usercentrics)</strong> — Cookie consent management.
              <a href="https://www.cookiebot.com/en/privacy-policy/" target="_blank" rel="noopener">
                Cookiebot Privacy Policy
              </a>.
            </li>
            <li>
              <strong>Amazon Associates and other affiliate programs</strong> — Governed by the
              respective retailer's privacy policy when you click through.
            </li>
            <li>
              <strong>Site hosting / CDN providers</strong> — May log standard server access data
              (IP addresses, browser type, access times) for security and operational purposes.
            </li>
          </ul>

          <h2>5. Your Rights</h2>

          <h3>EU / EEA / UK Residents (GDPR &amp; UK GDPR)</h3>
          <p>
            Under the General Data Protection Regulation (GDPR) and UK GDPR, you have the right to:
          </p>
          <ul>
            <li>
              <strong>Access</strong> — Request a copy of personal data I hold about you.
            </li>
            <li>
              <strong>Rectification</strong> — Request correction of inaccurate data.
            </li>
            <li>
              <strong>Erasure</strong> — Request deletion of your personal data ("right to be
              forgotten").
            </li>
            <li>
              <strong>Restriction</strong> — Request that I limit how I process your data.
            </li>
            <li>
              <strong>Objection</strong> — Object to processing based on legitimate interests.
            </li>
            <li>
              <strong>Portability</strong> — Request your data in a portable format.
            </li>
            <li>
              <strong>Withdraw consent</strong> — Where processing is based on consent, you may
              withdraw it at any time without affecting the lawfulness of prior processing.
            </li>
          </ul>
          <p>
            My legal basis for analytics processing is <em>consent</em> (via Cookiebot, before
            StatCounter cookies are set). For email correspondence, the legal basis is
            <em>performance of a request</em> (responding to your message).
          </p>
          <p>
            You also have the right to lodge a complaint with your local data protection authority
            (e.g., the ICO in the UK, or your EU member state's DPA).
          </p>

          <h3>California Residents (CCPA / CPRA)</h3>
          <p>Under the California Consumer Privacy Act (CCPA) and CPRA, you have the right to:</p>
          <ul>
            <li>Know what personal information is collected about you and how it is used.</li>
            <li>Request deletion of your personal information.</li>
            <li>
              Opt out of the sale of personal information —{" "}
              <strong>note: I do not sell personal information.</strong>
            </li>
            <li>Non-discrimination for exercising any of these rights.</li>
            <li>Correct inaccurate personal information I hold about you.</li>
          </ul>
          <p>
            To exercise these rights, contact:
            <a href="mailto:cole@projectsurfhelmet.com">cole@projectsurfhelmet.com</a>. I will
            respond within 45 days.
          </p>
          <p>
            If you have an unresolved complaint, you may contact the California Department of
            Consumer Affairs at 1625 North Market Blvd, Suite N 112, Sacramento, CA 95834 / (800)
            952-5210.
          </p>

          <h3>Canadian Residents (PIPEDA)</h3>
          <p>
            Under Canada's Personal Information Protection and Electronic Documents Act (PIPEDA),
            you have the right to access the personal information I hold about you and to request
            corrections. Contact me at
            <a href="mailto:cole@projectsurfhelmet.com">cole@projectsurfhelmet.com</a> to make a
            request.
          </p>

          <h3>Australian Residents (Australian Privacy Act 1988)</h3>
          <p>
            Under the Australian Privacy Act 1988 and the Australian Privacy Principles (APPs), you
            have the right to access and seek correction of personal information I hold about you.
            You also have the right to complain to the Office of the Australian Information
            Commissioner (OAIC) if you believe your privacy rights have been breached. Contact me
            first at <a href="mailto:cole@projectsurfhelmet.com">cole@projectsurfhelmet.com</a>.
          </p>

          <h3>Brazilian Residents (LGPD)</h3>
          <p>
            Under Brazil's Lei Geral de Proteção de Dados (LGPD), you have rights of access,
            correction, anonymization, portability, and deletion of your personal data. Contact me
            at <a href="mailto:cole@projectsurfhelmet.com">cole@projectsurfhelmet.com</a> to make a
            request.
          </p>

          <h3>All Other Visitors</h3>
          <p>
            Regardless of where you live, I aim to handle any personal data responsibly and
            minimally. If you have any concerns about your data, email me and I will do my best to
            address them.
          </p>

          <h2>6. Data Retention</h2>
          <ul>
            <li>
              <strong>Analytics data</strong> — Retained per the analytics provider's default
              retention settings (typically 14–26 months for Google Analytics).
            </li>
            <li>
              <strong>Contact form messages</strong> — Retained until the correspondence is
              resolved, then deleted.
            </li>
            <li>
              <strong>Server logs</strong> — Retained briefly by hosting infrastructure for security
              purposes, then automatically purged.
            </li>
          </ul>

          <h2>7. Data Security</h2>
          <p>
            I take reasonable precautions to protect data, including using HTTPS and secure hosting.
            However, no internet transmission is 100% secure. Please do not send sensitive personal
            information through this site's contact form.
          </p>

          <h2>8. International Data Transfers</h2>
          <p>
            This site is hosted in the United States. If you are visiting from outside the US, your
            data may be transferred to and processed in the US. Third-party services (e.g., Google
            Analytics) may also transfer data internationally per their own data transfer mechanisms
            (e.g., Standard Contractual Clauses for EU data).
          </p>

          <h2>9. Children's Privacy</h2>
          <p>
            This site is not directed at children under 13 (or under 16 for EU residents). I do not
            knowingly collect personal information from minors. If you believe a child has submitted
            data through this site, contact me and I will delete it promptly.
          </p>

          <h2>10. Links to Other Sites</h2>
          <p>
            This site links to third-party websites. I am not responsible for the privacy practices
            of any external site. Review each site's own privacy policy before providing any
            personal information.
          </p>

          <h2>11. Changes to This Policy</h2>
          <p>
            I may update this privacy policy at any time. The "Last Updated" date at the top of this
            page will reflect changes. For significant changes, I will make reasonable efforts to
            notify users (e.g., a notice on the site homepage).
          </p>

          <h2>12. Contact &amp; Rights Requests</h2>
          <p>To exercise any privacy rights, ask questions, or raise concerns about this policy:</p>
          <div class="contact-block">
            <strong>Cole Merrick</strong> — Project Surf Helmet<br />
            <a href="mailto:cole@projectsurfhelmet.com">cole@projectsurfhelmet.com</a>
            <br />
            <br />
            Response times: within 30 days generally; 45 days for CCPA requests; one month for GDPR
            requests (extendable by two months for complex requests, with notice).
          </div>
        </Content>
        {/*<Form />*/}
      </Article>
      <Footer />
    </Main>
  );
};

Support.propTypes = {
  classes: PropTypes.object.isRequired
};

export default injectSheet(styles)(Support);



// WEBPACK FOOTER //
// ./src/pages/privacy.js